import { NewBookingButton } from '@components/Common/NewBookingButton';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingProposalStatus } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { LoadingScreen } from 'noddi-ui-web';
import { CustomDateFormats, customerRoutes, hasErrorCode, noddiFormatDate } from 'noddi-util';
import { DepartmentOrganizationName } from 'noddi-web';
import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useCustomerDepartmentSelection } from '../../hooks/useCustomerDepartmentSelection';
import { useTrackProposalStep } from '../../hooks/useTrackProposalStep';
import { useBookingProposalSlug } from '../../stores/bookingProposalStore';
import { isRecallProposalType } from '../../utils/bookingProposalType';
import { showsSuggestedProposalTimes } from '../../utils/stationaryProposalPolicy';
import { getSuggestedTimesConfirmDeadline } from '../../utils/suggestedProposalTimes';
import { ProposalCard } from './ProposalCard';
import { StationaryProposalCard } from './StationaryProposalCard';

function getSubtitle({
  isRecallProposal,
  isStationaryProposal,
  showsSuggestedTimes
}: {
  isRecallProposal: boolean;
  isStationaryProposal: boolean;
  showsSuggestedTimes: boolean;
}) {
  if (showsSuggestedTimes) {
    // Named day rather than "within 24 hours" — a date is what people act on, so it
    // carries the sentence's only emphasis. Lowercase: it is mid-sentence Norwegian.
    const deadline = noddiFormatDate(getSuggestedTimesConfirmDeadline(), CustomDateFormats.FULL_WEEKDAY_DAY_DOT_MONTH);
    return (
      <Trans>
        We have set aside time for your tire change. Confirm one of the times below by{' '}
        <span className='font-semibold'>{deadline}</span>.
      </Trans>
    );
  }
  if (isStationaryProposal) {
    return t`Book your tire change at a time that suits you`;
  }
  if (isRecallProposal) {
    return t`It's time for your tire change again! We've reserved a spot for you`;
  }
  return t`Please review and confirm your proposed appointment with us`;
}

const ProposalClosedPage = ({
  title,
  message,
  showNewBookingButton
}: {
  title: string;
  message: ReactNode;
  showNewBookingButton?: boolean;
}) => (
  <ContentWrapperCustomerApp className='max-w-screen-md' hideBackButton title={title}>
    <div className='flex flex-col items-center gap-4 py-6 text-center'>
      <p className='text-muted-foreground'>{message}</p>
      {showNewBookingButton && (
        <NewBookingButton variant='success' actionText={t`Create new booking`} hideIcon defaultSize />
      )}
    </div>
  </ContentWrapperCustomerApp>
);

// A component rather than a hook call in `BookingProposal`: the step counts only
// once the live proposal card renders, and that decision sits below several
// early returns for closed, expired and rejected proposals.
const ProposalLandingStepTracker = () => {
  useTrackProposalStep('landing');

  return null;
};

export const BookingProposal = () => {
  const slug = useBookingProposalSlug();
  const { getSelectedDepartmentForGroup } = useCustomerDepartmentSelection();
  const {
    data: bookingProposal,
    isPending: isBookingProposalPending,
    error: bookingProposalError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingProposalBySlug,
    input: { slug }
  });

  if (isBookingProposalPending) {
    return <LoadingScreen />;
  }

  if (bookingProposalError) {
    if (hasErrorCode(bookingProposalError, 'booking_proposal_closed_by_staff')) {
      return (
        <ProposalClosedPage
          title={t`Proposal closed`}
          message={<Trans>This proposal has been closed. You are welcome to create a new booking.</Trans>}
          showNewBookingButton
        />
      );
    }

    if (hasErrorCode(bookingProposalError, 'booking_proposal_rejected')) {
      return (
        <ProposalClosedPage
          title={t`Proposal rejected`}
          message={<Trans>You have rejected this proposal, but you are welcome to create a new booking.</Trans>}
          showNewBookingButton
        />
      );
    }

    return <ApiErrorPageWithTrans error={bookingProposalError} />;
  }

  const status = bookingProposal.status.value;

  if (status === BookingProposalStatus.DRAFT) {
    // TODO: Show proper expired content here instead of a generic closed page
    return (
      <ProposalClosedPage
        title={t`Proposal not available`}
        message={<Trans>This proposal is not yet available. You are welcome to create a new booking.</Trans>}
        showNewBookingButton
      />
    );
  }

  if (status === BookingProposalStatus.CLOSED_BOOKING_CREATED) {
    const redirectTo = bookingProposal.booking
      ? customerRoutes.bookingInfo.getPath({ slug: bookingProposal.booking.slug })
      : customerRoutes.homepage.getPath();
    return <Navigate to={redirectTo} replace />;
  }

  if (status === BookingProposalStatus.CLOSED_BY_STAFF || status === BookingProposalStatus.CLOSED_BY_SYSTEM_EXPIRED) {
    return (
      <ProposalClosedPage title={t`Proposal closed`} message={<Trans>This proposal is no longer available.</Trans>} />
    );
  }

  if (status === BookingProposalStatus.REJECTED_BY_CUSTOMER) {
    return (
      <ProposalClosedPage
        title={t`Proposal rejected`}
        message={<Trans>You have rejected this proposal, but you are welcome to create a new booking.</Trans>}
        showNewBookingButton
      />
    );
  }

  const isRecallProposal = isRecallProposalType(bookingProposal.type.value);

  const isStationaryProposal = bookingProposal.bookingLocationType === 'STATIONARY';
  const showsSuggestedTimes =
    isStationaryProposal && showsSuggestedProposalTimes(bookingProposal.serviceDepartment.brand.domain);
  const isOrganizationProposal = bookingProposal.userGroupMember.userGroup.isOrganization;
  const selectedDepartment = getSelectedDepartmentForGroup(bookingProposal.userGroupMember.userGroup.id);
  const departmentName = bookingProposal.customerDepartmentName || selectedDepartment?.name;
  const organizationName = bookingProposal.customerOrganizationName || bookingProposal.userGroupMember.userGroup.name;

  return (
    <ContentWrapperCustomerApp
      className='max-w-screen-md'
      hideBackButton
      title={`${t`Hi`} ${bookingProposal.userGroupMember.user.firstName}!`}
      subtitle={getSubtitle({ isRecallProposal, isStationaryProposal, showsSuggestedTimes })}
    >
      <ProposalLandingStepTracker />
      <div className='flex flex-col gap-4'>
        {isOrganizationProposal && (
          <p className='flex items-start gap-1.5 text-sm text-muted-foreground'>
            <NoddiIcon name='Building2' size='small' />
            <span>
              <Trans>On behalf of</Trans>
            </span>
            {departmentName ? (
              <DepartmentOrganizationName departmentName={departmentName} organizationName={organizationName} />
            ) : (
              organizationName
            )}
          </p>
        )}
        {isStationaryProposal ? (
          <StationaryProposalCard proposal={bookingProposal} />
        ) : (
          <ProposalCard proposal={bookingProposal} />
        )}
      </div>
    </ContentWrapperCustomerApp>
  );
};
